import type { Rect } from '../../engine/hex/types';
import type { HexMapState } from '../../engine/render/renderer';
import type { Settlement } from '../../engine/settlement';
import type { ModalTheme } from './ModalTheme';
import { getResourceIconId } from '../../ui/overlay/assets/resourceIcons';

/**
 * Draws the resource cost tooltip for building entries in the settlement modal.
 * Kept separate from modal rendering to keep UI logic modular.
 */
export function drawBuildingCostTooltip(
    ctx: CanvasRenderingContext2D,
    state: HexMapState,
    theme: ModalTheme,
    modalRect: Rect,
    hover: { entry: { costs: Record<string, number> }; rect: Rect },
    settlement: Settlement,
): void {
    const entries = Object.entries(hover.entry.costs || {});
    if (entries.length === 0) return;
    const tooltip = theme.costTooltip;
    const maxColumns = Math.max(1, tooltip.columns);
    const rowCount = tooltip.rows;
    const columns = Math.max(1, Math.min(maxColumns, entries.length));
    const width =
        tooltip.padding * 2 + columns * tooltip.cellW + tooltip.gap * Math.max(0, columns - 1);
    const height =
        tooltip.padding * 2 + rowCount * tooltip.cellH + tooltip.gap * Math.max(0, rowCount - 1);

    let x = hover.rect.x + hover.rect.w + 8;
    let y = hover.rect.y;
    const maxX = modalRect.x + modalRect.w - width - 8;
    const maxY = modalRect.y + modalRect.h - height - 8;
    if (x > maxX) x = Math.max(modalRect.x + 8, hover.rect.x - width - 8);
    y = Math.min(Math.max(modalRect.y + 8, y), maxY);

    ctx.save();
    ctx.fillStyle = theme.colors.tabInactiveFill;
    ctx.strokeStyle = theme.colors.tabStroke;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.rect(x, y, width, height);
    ctx.fill();
    ctx.stroke();

    ctx.textBaseline = 'middle';
    ctx.font = theme.fonts.muted;

    entries.slice(0, columns * rowCount).forEach(([key, amount], index) => {
        const col = index % columns;
        const row = Math.floor(index / columns);
        const cx = x + tooltip.padding + col * (tooltip.cellW + tooltip.gap);
        const cy = y + tooltip.padding + row * (tooltip.cellH + tooltip.gap);
        const iconId = getResourceIconId(key);
        const icon = iconId ? state.overlayAssets.icons.get(iconId) : null;
        let available = 0;
        if (key === 'tools') {
            available = settlement.inventory.tools.length;
        } else {
            const inv = settlement.inventory as unknown as Record<string, number>;
            available = inv[key] ?? 0;
        }
        const canAfford = available >= amount;
        const color = canAfford ? theme.colors.costGood : theme.colors.costBad;

        if (icon && state.overlayAssets.icons.isReady(iconId!)) {
            ctx.drawImage(
                icon,
                cx,
                cy + (tooltip.cellH - tooltip.iconSize) / 2,
                tooltip.iconSize,
                tooltip.iconSize,
            );
        }
        ctx.fillStyle = color;
        ctx.fillText(String(amount), cx + tooltip.iconSize + 4, cy + tooltip.cellH / 2);
    });

    ctx.restore();
}
